import { supabase } from './supabaseClient';
import { Task, TaskSubmission, VerificationType } from '../types/task';

export class VerificationService {
  /**
   * Generates a unique referral code for task verification
   */
  private static generateReferralCode(): string {
    const prefix = 'SeedDots';
    const randomCode = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `${prefix}-${randomCode}`;
  }

  /**
   * Generates verification details for a task
   */
  static async generateVerificationDetails(
    verificationType: VerificationType,
    textResponseQuestion?: string
  ): Promise<any> {
    const verification_details: {
      status: 'pending_verification';
      expected_response?: string;
      verification_question?: string;
      field_label?: string;
    } = {
      status: 'pending_verification'
    };

    // For the new verification types, we set up the appropriate details
    if (verificationType === 'text_response') {
      verification_details.verification_question = textResponseQuestion || '';
    } else if (verificationType === 'email_username') {
      verification_details.field_label = 'Email/Username';
    }

    return verification_details;
  }

  /**
   * Creates a new task with verification settings
   */
  static async createTask(taskData: {
    title: string;
    description: string;
    points: number;
    instructions: string;
    category: string;
    tags: string[];
    created_by: string;
    verification_type: VerificationType;
    verification_details: any;
    max_participants?: number;
    country_code: string;
  }): Promise<{ task: Task | null; error: any }> {
    try {
      const newTask = {
        ...taskData,
        status: 'open' as const,
        current_participants: 0,
      };

      const { data, error } = await supabase
        .from('tasks')
        .insert([newTask])
        .select()
        .single();

      if (error) throw error;

      return { task: data as Task, error: null };
    } catch (error) {
      console.error('Create task error:', error);
      return { task: null, error };
    }
  }

  /**
   * Verifies a task using a referral code
   */
  static async verifyReferralCode(taskId: string, referralCode: string): Promise<{ success: boolean; error: any }> {
    try {
      // Get the task
      const { data: task, error: taskError } = await supabase
        .from('tasks')
        .select('*')
        .eq('id', taskId)
        .single();

      if (taskError) throw taskError;
      if (!task) throw new Error('Task not found');

      // Check if the task uses referral code verification
      if (task.verification_type !== 'referral_code') {
        throw new Error('This task does not use referral code verification');
      }

      // Check if the referral code matches
      const isValid = task.verification_details?.referral_code === referralCode;

      return { success: isValid, error: null };
    } catch (error) {
      console.error('Verify referral code error:', error);
      return { success: false, error };
    }
  }

  /**
   * Checks if a user has already submitted a verification for this task
   */
  static async hasUserSubmittedTask(taskId: string, userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('task_submissions')
        .select('id')
        .eq('task_id', taskId)
        .eq('user_id', userId);

      if (error) throw error;

      return data && data.length > 0;
    } catch (error) {
      console.error('Check user submission error:', error);
      return false;
    }
  }

  /**
   * Uploads a screenshot for verification
   * 
   * Note: Before using this function, ensure you have created a 'screenshots' bucket
   * in the Supabase dashboard and configured appropriate RLS policies.
   * See STORAGE_SETUP.md for instructions.
   */
  static async uploadScreenshot(taskId: string, userId: string, screenshot: File): Promise<{ url: string | null; error: any }> {
    try {
      console.log('Starting screenshot upload process...', { taskId, userId });

      if (!screenshot) {
        return { url: null, error: 'No file provided' };
      }

      if (!taskId || !userId) {
        return { url: null, error: 'Task ID and User ID are required' };
      }

      // Validate the file type is an image
      if (!screenshot.type.startsWith('image/')) {
        return { url: null, error: 'Only image files are accepted' };
      }

      // Get file extension from mime type
      const fileExtension = screenshot.type.split('/')[1] || 'jpg';

      // Generate a file name that's unique and preserves the file extension
      const fileName = `${taskId}/${userId}/${Date.now()}.${fileExtension}`;

      // Log the file being uploaded
      console.log('Attempting to upload:', {
        bucket: 'screenshots',
        fileName,
        fileSize: screenshot.size,
        fileType: screenshot.type
      });

      // Attempt to upload the file
      const { data, error } = await supabase
        .storage
        .from('screenshots')
        .upload(fileName, screenshot, {
          contentType: screenshot.type, // Set the proper content type
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Supabase storage upload error:', error);

        if (error.message?.includes('bucket') || error.message?.includes('404')) {
          return {
            url: null,
            error: 'Storage bucket not found. The administrator needs to create the "screenshots" bucket in Supabase.'
          };
        }

        if (error.message?.includes('403') || error.message?.includes('security policy')) {
          return {
            url: null,
            error: 'Permission denied. Make sure you are logged in and have permission to upload files.'
          };
        }

        throw error;
      }

      // Return the path of the uploaded file, not the public URL
      // The 'url' key here will now hold the file path for the caller to use.
      return { url: fileName, error: null };
    } catch (error: any) {
      console.error('Upload screenshot error:', error);
      return {
        url: null,
        error: error.message || 'Failed to upload screenshot. Please try again.'
      };
    }
  }

  /**
   * Submits a task verification
   */
  static async submitTaskVerification(submission: Omit<TaskSubmission, 'id' | 'submitted_at' | 'status'>): Promise<{ submission: TaskSubmission | null; error: any }> {
    try {
      // Check if the user has already submitted a verification for this task
      const hasSubmitted = await this.hasUserSubmittedTask(submission.task_id, submission.user_id);

      if (hasSubmitted) {
        // If already submitted, use updateTaskVerification instead
        return this.updateTaskVerification(submission);
      }

      // Create the submission
      const { data, error } = await supabase
        .from('task_submissions')
        .insert([{
          ...submission,
          submitted_at: new Date(),
          status: 'pending',
        }])
        .select()
        .single();

      if (error) throw error;

      // Manually increment applied_count since trigger might not be working
      // First get current task data
      const { data: currentTask, error: taskFetchError } = await supabase
        .from('tasks')
        .select('applied_count, status')
        .eq('id', submission.task_id)
        .single();

      if (!taskFetchError && currentTask) {
        // Update with incremented applied_count
        const { error: updateCountError } = await supabase
          .from('tasks')
          .update({
            status: 'pending_verification',
            applied_count: (currentTask.applied_count || 0) + 1
          })
          .eq('id', submission.task_id);

        if (updateCountError) {
          console.error('Error updating applied_count:', updateCountError);
        }
      } else {
        // Fallback: just update status
        await supabase
          .from('tasks')
          .update({ status: 'pending_verification' })
          .eq('id', submission.task_id);
      }

      // Wait a moment for all updates to complete
      await new Promise(resolve => setTimeout(resolve, 100));

      return { submission: data as TaskSubmission, error: null };
    } catch (error) {
      console.error('Submit verification error:', error);
      return { submission: null, error };
    }
  }

  /**
   * Updates an existing task verification
   */
  static async updateTaskVerification(submission: Omit<TaskSubmission, 'id' | 'submitted_at' | 'status'>): Promise<{ submission: TaskSubmission | null; error: any }> {
    try {
      // Get the existing submission
      const { data: existingData, error: fetchError } = await supabase
        .from('task_submissions')
        .select('*')
        .eq('task_id', submission.task_id)
        .eq('user_id', submission.user_id)
        .single();

      if (fetchError) throw fetchError;
      if (!existingData) throw new Error('Submission not found');

      // Update the submission with new proof
      const { data, error } = await supabase
        .from('task_submissions')
        .update({
          proof: submission.proof,
          submitted_at: new Date(),
          status: 'pending', // Reset to pending for review
          feedback: null, // Clear previous feedback
          reviewed_by: null,
          reviewed_at: null
        })
        .eq('id', existingData.id)
        .select()
        .single();

      if (error) throw error;

      // Update the task status to pending_verification
      await supabase
        .from('tasks')
        .update({ status: 'pending_verification' })
        .eq('id', submission.task_id);

      return { submission: data as TaskSubmission, error: null };
    } catch (error) {
      console.error('Update verification error:', error);
      return { submission: null, error };
    }
  }

  /**
   * Reviews a task submission (approve or reject)
   */
  static async reviewSubmission(
    taskId: string,
    userId: string,
    approved: boolean,
    reviewerId: string,
    feedback?: string
  ): Promise<{ success: boolean; error: any }> {
    try {
      // Get the submission
      const { data: submissions, error: submissionError } = await supabase
        .from('task_submissions')
        .select('*')
        .eq('task_id', taskId)
        .eq('user_id', userId);

      if (submissionError) throw submissionError;
      if (!submissions || submissions.length === 0) throw new Error('Submission not found');

      const submission = submissions[0] as TaskSubmission;

      // Check if submission is already reviewed (approved or rejected)
      if (submission.status !== 'pending') {
        return {
          success: false,
          error: `Submission has already been ${submission.status}. Cannot review again.`
        };
      }

      // Get task details including max_participants and current_participants
      const { data: task, error: taskError } = await supabase
        .from('tasks')
        .select('*, current_participants, max_participants')
        .eq('id', taskId)
        .single();

      if (taskError) throw taskError;
      if (!task) throw new Error('Task not found');

      if (approved) {
        try {
          // Try to use atomic database function to approve submission
          const { data: approvalResult, error: approvalError } = await supabase.rpc('approve_task_submission', {
            p_submission_id: submission.id,
            p_task_id: taskId,
            p_reviewer_id: reviewerId,
            p_feedback: feedback
          });

          if (approvalError) {
            // If function doesn't exist, fall back to original method
            if (approvalError.code === 'PGRST202') {
              console.log('approve_task_submission function not found, using fallback method');
              return await this.fallbackApproveSubmission(taskId, userId, reviewerId, feedback, submission, task);
            }
            throw approvalError;
          }

          if (!approvalResult?.success) {
            return { success: false, error: approvalResult?.error || 'Failed to approve submission' };
          }
        } catch (error: any) {
          // If RPC call fails, fall back to original method
          if (error.code === 'PGRST202') {
            console.log('approve_task_submission function not found, using fallback method');
            return await this.fallbackApproveSubmission(taskId, userId, reviewerId, feedback, submission, task);
          }
          throw error;
        }

      } else {
        try {
          // Try to use atomic database function to reject submission
          const { data: rejectionResult, error: rejectionError } = await supabase.rpc('reject_task_submission', {
            p_submission_id: submission.id,
            p_task_id: taskId,
            p_reviewer_id: reviewerId,
            p_feedback: feedback
          });

          if (rejectionError) {
            // If function doesn't exist, fall back to original method
            if (rejectionError.code === 'PGRST202') {
              console.log('reject_task_submission function not found, using fallback method');
              return await this.fallbackRejectSubmission(taskId, reviewerId, feedback, submission);
            }
            throw rejectionError;
          }

          if (!rejectionResult?.success) {
            return { success: false, error: rejectionResult?.error || 'Failed to reject submission' };
          }
        } catch (error: any) {
          // If RPC call fails, fall back to original method
          if (error.code === 'PGRST202') {
            console.log('reject_task_submission function not found, using fallback method');
            return await this.fallbackRejectSubmission(taskId, reviewerId, feedback, submission);
          }
          throw error;
        }
      }

      return { success: true, error: null };
    } catch (error: any) {
      console.error('Review submission error:', error);
      return { success: false, error };
    }
  }

  /**
   * Handles when a task reaches maximum participants - marks task as completed and rejects pending submissions
   */
  private static async handleTaskFullAndRejectPending(taskId: string, approvedSubmissionId: string, rejectionReason: string): Promise<void> {
    try {
      // Update task status to completed
      const { error: taskUpdateError } = await supabase
        .from('tasks')
        .update({ status: 'completed' })
        .eq('id', taskId);

      if (taskUpdateError) throw taskUpdateError;

      // Reject all other pending submissions for this task
      const { error: rejectPendingError } = await supabase
        .from('task_submissions')
        .update({
          status: 'rejected',
          reviewed_at: new Date(),
          feedback: rejectionReason
        })
        .eq('task_id', taskId)
        .eq('status', 'pending')
        .neq('id', approvedSubmissionId);

      if (rejectPendingError) throw rejectPendingError;

    } catch (error) {
      console.error('Error handling task full scenario:', error);
      throw error;
    }
  } // End of reviewSubmission

  /**
   * Generates a new API verification webhook URL for a task
   */
  static async generateApiWebhook(taskId: string): Promise<{ webhook_url: string | null; error: any }> {
    try {
      const webhook_id = Math.random().toString(36).substring(2, 15);
      const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'YOUR_BASE_URL_HERE'; // User may need to configure this if window is not available
      const webhook_url = `${baseUrl}/api/webhook/${webhook_id}`;

      const { error } = await supabase
        .from('tasks')
        .update({
          verification_details: { // This overwrites verification_details. Consider merging if other fields are important.
            webhook_id,
            webhook_url
          }
        })
        .eq('id', taskId);

      if (error) throw error;

      return { webhook_url, error: null };
    } catch (error: any) {
      console.error('Generate API webhook error:', error);
      return { webhook_url: null, error };
    }
  } // End of generateApiWebhook

  /**
   * Fallback method for approving submissions when atomic function is not available
   */
  private static async fallbackApproveSubmission(
    taskId: string,
    userId: string,
    reviewerId: string,
    feedback: string | undefined,
    submission: TaskSubmission,
    task: any
  ): Promise<{ success: boolean; error: any }> {
    try {
      // Check if task has reached max participants
      if (task.max_participants && task.current_participants >= task.max_participants) {
        // Reject this submission and all other pending submissions
        await this.handleTaskFullAndRejectPending(taskId, submission.id, 'Task has reached maximum number of participants');
        return { success: false, error: 'Task has reached maximum number of participants' };
      }

      // Update the submission status to approved
      const { error: updateError } = await supabase
        .from('task_submissions')
        .update({
          status: 'approved',
          reviewed_by: reviewerId,
          reviewed_at: new Date(),
          feedback,
        })
        .eq('id', submission.id);

      if (updateError) throw updateError;

      // Increment participant count - REMOVED to prevent double counting
      // The trigger on task_submissions update already handles this
      /*
      const { error: incrementParticipantsError } = await supabase.rpc('increment_task_participants', {
        task_id_in: taskId,
        amount_in: 1
      });
      if (incrementParticipantsError) throw incrementParticipantsError;
      */

      // Increment user's completed tasks
      const { error: incrementCompletedTasksError } = await supabase.rpc('increment_user_completed_tasks', {
        user_id_in: userId,
        amount_in: 1
      });
      if (incrementCompletedTasksError) throw incrementCompletedTasksError;

      // Check if task should be marked as completed
      const newParticipantCount = (task.current_participants || 0) + 1;
      if (task.max_participants && newParticipantCount >= task.max_participants) {
        // Mark task as completed and reject all remaining pending submissions
        await this.handleTaskFullAndRejectPending(taskId, submission.id, 'Task has reached maximum number of participants');
      } else {
        // Just update task status to pending_verification (in case there are more submissions to review)
        const { error: taskUpdateError } = await supabase
          .from('tasks')
          .update({ status: 'pending_verification' })
          .eq('id', taskId);
        if (taskUpdateError) throw taskUpdateError;
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Fallback approve submission error:', error);
      return { success: false, error };
    }
  }

  /**
   * Fallback method for rejecting submissions when atomic function is not available
   */
  private static async fallbackRejectSubmission(
    taskId: string,
    reviewerId: string,
    feedback: string | undefined,
    submission: TaskSubmission
  ): Promise<{ success: boolean; error: any }> {
    try {
      // Update the submission status to rejected
      const { error: updateError } = await supabase
        .from('task_submissions')
        .update({
          status: 'rejected',
          reviewed_by: reviewerId,
          reviewed_at: new Date(),
          feedback,
        })
        .eq('id', submission.id);

      if (updateError) throw updateError;

      // If rejected, update task status back to open
      const { error: taskRejectError } = await supabase
        .from('tasks')
        .update({ status: 'open' })
        .eq('id', taskId);
      if (taskRejectError) throw taskRejectError;

      return { success: true, error: null };
    } catch (error) {
      console.error('Fallback reject submission error:', error);
      return { success: false, error };
    }
  }
} // End of VerificationService class
